// Official GA documentation: https://developers.google.com/analytics/devguides/collection/gtagjs/events
import router from '@/router'
import isPlainObject from 'lodash.isplainobject'
import isString from 'lodash.isstring'
import { getSubdomain } from '@/services/utils'

/**
 * @function    analyticsEventsTracker
 * Evaluates data structure of errorData and utilizes helper functions appropriately,
 * yielding GA tracking for each error as a separate event
 * @param   {String}  action  - custom string that functions as action being tracked
 * @param   {String | Array | Object (plain)}  errorData  - form error data received from API response
 * @param   {String}  pageTitle - the title of the page, e.g. 'Home'
 * @param   {String}  pagePath - the path of the page, e.g. '/'
 **/
export function analyticsEventsTracker(action, errorData, pageTitle, pagePath) {
  if (isString(errorData)) {
    trackEvent(action, errorData, pageTitle, pagePath)
  }

  if (Array.isArray(errorData)) {
    errorData.forEach(error => trackEvent(action, error, pageTitle, pagePath))
  }

  if (isPlainObject(errorData)) {
    Object.keys(errorData).forEach(field =>
      trackEventsWithField(action, errorData[field], field, pageTitle, pagePath),
    )
  }
}

/**
 * @function    trackEvent
 * Sends a GA Event, with proper action and error, and leveraging the currentRoute's name
 * as the GA Event's category, for more intuitive analysis by client
 * @param   {String}  action  - custom string that functions as action being tracked
 * @param   {String}  error  - form error string
 * @param   {String}  pageTitle - the title of the page, e.g. 'Home'
 * @param   {String}  pagePath - the path of the page, e.g. '/'
 **/
export function trackEvent(action, error, pageTitle, pagePath) {
  let {
    app: { $gtag },
  } = router

  let outboundParams = {
    event_category: pageTitle,
    event_label: error,
  }
  $gtag.query('set', 'dimension1', getSubdomain())
  $gtag.query('set', 'dimension2', pagePath)
  $gtag.event(action, outboundParams)
}

/**
 * @function    trackEventsWithField
 * Iterates through formErrors, building an error {String} that incorporates the field arg
 * then passes this computed string and the action arg to trackEvent() for sending to GA
 * yielding GA tracking for each error as a separate event
 * @param   {String}  action  - custom string that functions as action being tracked
 * @param   {Array}  formErrors  - array of form errors all associated with the field arg
 * @param   {String}  field  - form field the formErrors are all associated with
 * @param   {String}  pageTitle - the title of the page, e.g. 'Home'
 * @param   {String}  pagePath - the path of the page, e.g. '/'
 **/
function trackEventsWithField(action, formErrors, field, pageTitle, pagePath) {
  for (let i = 0; i < formErrors.length; ++i) {
    let error = field + ': ' + formErrors[i]
    trackEvent(action, error, pageTitle, pagePath)
  }
}

/**
 * @const    analyticsEventsMixer
 * mixin for components that require time tracking & bounce tracking
 **/
export const analyticsMixin = {
  methods: {
    /**
     * @function    eventsTracker
     * makes use of the component's routeName and analyticsEventsTracker instance,
     * so that calling analticsEventsTracker is more accessible and clean
     * @param   {String}  action  - custom string that functions as action of event being tracked
     * @param   {String}  label  - custom string that functions as label of event being tracked
     **/
    eventsTracker(action, label = '') {
      let { pageTitle, pagePath } = getPageTitleAndPath(
        this.$route,
        this.$store.getters.userIsLoggedIn,
      )
      analyticsEventsTracker(action, label, pageTitle, pagePath)
    },
    /**
     * @function    trackClickedCTA
     * Emits an often-tracked event from the application: a call to aciton (CTA) has been clicked
     * @param {String} label    - optional param to be used as event's label
     **/
    trackClickedCTA(label) {
      this.eventsTracker('clicked-cta', isString(label) ? label : '')
    },
    // In case a custom pageview is desired (as in ResumeStartedRequest).
    trackCustomPageview(pageTitle, pagePath) {
      this.$gtag.query('set', 'dimension1', getSubdomain())
      this.$gtag.query('set', 'dimension2', pagePath)
      this.$gtag.pageview({
        page_title: pageTitle,
        page_path: cleanPath(pagePath),
      })
    },
    // In case the original analyticsEventsTracker is desired (as in ResumeStartedRequest).
    analyticsEventsTracker,
  },
}

/**
 * @const    pagesWithAuthTracking
 * There is a special circumstance when it comes to tracking certain pageviews: userIsLoggedIn
 * Now that the tracking of more than just the homepage are subject to auth-differentiated pageview tracking,
 * below is a hash table that can be updated for further additions/removals when it comes to this special tracking circumstance.
 * @keys     name of route to include for auth-differentiated pageview tracking
 * @values   any truthy value -- preferably just the Boolean
 **/
export const pagesWithAuthTracking = {
  Home: true,
  AddPayment: true,
  DonationReview: true,
  DonationSummary: true,
  PayrollReview: true,
  PayrollSummary: true,
}

/**
 * @const    pagesToIgnore
 * If there is a special circumstance that requires the pageview to be tracked at the view/component level,
 * then the page can be added herein and therefore ignored from the standard page-view tracking protocol.
 * @keys     name of route to ignore
 * @values   any truthy value -- preferably just the Boolean
 **/
export const pagesToIgnore = {
  RequestSummary: true,
  SurveyResponseDetail: true,
  PasswordResetConfirm: true,
  AddPayment: true,
}

/**
 * @const    pagesWithCustomPath
 * Custom function to generate page title and page path for specific pages.
 * @keys     name of route to ignore
 * @values   custom function to run
 **/
export const pagesWithCustomPath = {
  Home: customPathForHome,
  FAQ: customPathForFAQ,
}

/**
 * @function    cleanPath
 * lower case and remove trailing slash
 * @param {String}  pathString route path
 **/
export function cleanPath(pathString) {
  let p = pathString.toLowerCase()
  return p[p.length - 1] === '/' ? p.slice(0, p.length - 1) : p
}

export function getPageTitleAndPath(route, userIsLoggedIn) {
  // If page has custom title/path defined by a custom function, trigger that instead
  if (route.name in pagesWithCustomPath) {
    return pagesWithCustomPath[route.name](route, userIsLoggedIn)
  }

  // If auth should be tracked on a page, customize path
  if (pagesWithAuthTracking[route.name]) {
    return {
      pageTitle: route.name,
      pagePath: pagePathWithAuthTracking(route.path, userIsLoggedIn),
    }
  }

  // Otherwise, return normal title + path
  return {
    pageTitle: route.name,
    pagePath: cleanPath(route.path),
  }
}

/* Append authentication info as a suffix to a page path. */
export function pagePathWithAuthTracking(routePath, userIsLoggedIn) {
  const authSuffix = userIsLoggedIn ? '-post-auth' : '-pre-auth'
  return cleanPath(routePath) + authSuffix
}

/**
 * @function    customPathForHome
 * @param {Object} route
 * @param {Boolean} userIsLoggedIn
 **/
function customPathForHome(route, userIsLoggedIn) {
  // Specify unique pagePath if a screen has been routed to immediately following onboarding.
  const { postOnboarding } = route.params
  const authSuffix = userIsLoggedIn ? ' (Post-Auth)' : ' (Pre-Auth)'
  const pageTitle = postOnboarding ? route.name + ' (Post-Onboarding)' : route.name + authSuffix
  const pagePath = postOnboarding ? '/home-post-onboarding' : route.path
  return { pageTitle, pagePath }
}

/**
 * @function    customPathForFAQ
 * @param {Object} route
 * @param {Boolean} userIsLoggedIn
 **/
function customPathForFAQ(route, userIsLoggedIn) {
  // Special circumstance on the FAQ page: did user navigate to the overall FAQs or to a specific Q
  return {
    pageTitle: route.hash ? route.name + ' ' + `(Question ${route.hash})` : route.name,
    pagePath: route.hash ? cleanPath(route.fullPath) : cleanPath(route.path),
  }
}
